/**
 * Store - the classic interface's state
 *
 * Screen state (focus, selection, current view, zoom, panels, filters…) and
 * a mirror of the open map's state, which the engine owns since Phase 5b,
 * step 3 (engine/map-state.js: the tree, the map's id, sharing). Views read
 * both here and subscribe to their changes; the map's data changes only
 * through the engine.
 */

import { emit, on, Events } from "../../../engine/core/events.js";
import { normalizeHorizon } from "../../../engine/core/horizon.js";
import { normalizeViewName } from "../../../engine/core/view-names.js";
import * as dataModel from "../../../engine/core/data-model.js";
import * as MapState from "../../../engine/map-state.js";
import { isDocQuotaBlocked } from "../../../engine/services/quota-state.js";
import { isMapFull as mapIsFull } from "../../../engine/services/map-size.js";
import { getDocState } from "../../../shared/local-doc-state.js";
import { createLogger } from "../../../engine/core/logger.js";
import { UNTAGGED_ID, isNotTaggedId } from "../../../engine/core/tag-filter.js";

// ============================================
// Logging Configuration (using shared logger)
// ============================================
const log = createLogger("Store", "warn");

// Convenience aliases for internal use
const logError = (...args) => log.error(...args);
const logWarn = (...args) => log.warn(...args);
const logInfo = (...args) => log.info(...args);
const logDebug = (...args) => log.debug(...args);

/**
 * Set the log level at runtime
 * @param {string|number} level - 'none'|'error'|'warn'|'info'|'debug' or 0-4
 */
export function setLogLevel(level) {
  log.setLevel(level);
}

// Synthetic tag id for "nodes with no tags". Lives alongside real tag ids in
// hiddenTagIds / highlightedTagIds so users can hide or highlight untagged
// nodes from the same UI surface as real tags. Intentionally a string that
// can't collide with a real (UUID-based) tag id.
export { UNTAGGED_ID };

/**
 * Whether a node has no real tag references. Treats malformed entries
 * (missing tagId) as not-a-tag, matching how the rest of the codebase
 * extracts tag ids.
 * @param {Object} node
 */
export function isUntagged(node) {
  if (!node || !Array.isArray(node.tags) || node.tags.length === 0) return true;
  for (const t of node.tags) {
    const id = typeof t === "string" ? t : t?.tagId;
    if (id) return false;
  }
  return true;
}



// ============================================
// State Shape
// ============================================

const initialState = {
  // Tree data
  data: null, // Root node of the tree

  // Navigation
  focusId: null, // Currently focused node (zoom target) - set by initialize()
  selectedId: null, // Currently selected node
  hoveredId: null, // Currently hovered node

  // View state
  currentView: "dashboard", // Active view name
  hideCompleted: false, // Hide completed tasks

  // Gantt specific
  ganttScale: "auto", // day, week, month, auto
  ganttZoom: 1, // Zoom level
  ganttRowHeight: 100, // Row height as percentage (100% = 32px)
  userSetProjectDates: false, // User manually set project dates

  // Mindmap specific
  mindmapGap: 50, // Vertical gap as percentage of node height
  mindmapLayoutMode: "balanced", // Level-0 side placement: balanced | right | left

  // View transform state (zoom/pan)
  mindmapTransform: { zoom: 1, panX: 0, panY: 0 },
  // Bubble's saved view on this device; null until there is one (the view
  // then fits the map: interfaces/classic/views/bubble/index.js).
  bubbleTransform: null,

  // UI state
  sidebarCollapsed: false,
  notesPanelOpen: false,
  currentNoteId: null, // Currently open note in notes panel/view
  modalOpen: false,
  editingNodeId: null,

  // User/auth (reference only - actual auth state in auth module)
  currentUser: null,
  currentDocId: null,

  // Sharing state
  // isShared: true when the current focus/root node has a shareId attached
  // This indicates the user is viewing shared content, regardless of ownership
  // Used by UI to show sharing indicators and control access
  isShared: false,
  shareRole: null,  // "owner" | "editor" | "viewer" | null - current user's role

  // Tag filtering/highlighting (local UI state, not synced)
  hiddenTagIds: [],       // tag IDs whose nodes are hidden from view
  highlightedTagIds: [],  // tag IDs whose nodes get color highlight
  tagDimLevel: 80,        // 0-100, how much to dim non-matching nodes in spotlight mode
  // Horizon cut-off (local UI state, per document) — see engine/core/horizon.js
  horizon: { past: null, ahead: null, inExecutionOnly: false },
};

let state = { ...initialState };

// Configurable "is node done" checker (injected by main.js, checks tags + legacy status)
let _nodeIsDoneFn = (node) => node.status === 'done'; // default: legacy check only

/**
 * Set the function used to determine if a node is "done".
 * Called by main.js after CRDTBridge is initialized.
 * @param {Function} fn - (node) => boolean
 */
export function setNodeIsDoneFn(fn) {
  _nodeIsDoneFn = fn;
}

// Subscribers
const subscribers = new Set();

// Track linked document references
// ============================================
// Core Store Functions
// ============================================

/**
 * Get current state (read-only snapshot)
 * @returns {Object} Current state
 */
export function getState() {
  return { ...state };
}

/**
 * Get a specific state value
 * @param {string} key - State key (supports dot notation)
 * @returns {*} Value
 */
export function get(key) {
  if (key.includes(".")) {
    return key.split(".").reduce((obj, k) => obj?.[k], state);
  }
  return state[key];
}

/**
 * Subscribe to state changes
 * @param {Function} callback - Called with (state, prevState, action)
 * @returns {Function} Unsubscribe function
 */
export function subscribe(callback) {
  subscribers.add(callback);

  // Phase 3.4 (PLAN17): Enhanced warning with diagnostic info
  if (subscribers.size > 50) {
    logWarn(`High subscriber count (${subscribers.size}) - potential memory leak`);
    // Provide diagnostic info about subscriber sources
    const subscriberNames = Array.from(subscribers)
      .map(s => s.name || 'anonymous')
      .slice(0, 10);
    logWarn(`First 10 subscriber names:`, subscriberNames);
  }

  return () => subscribers.delete(callback);
}

/**
 * Internal: Update state and notify subscribers
 * Phase 1.3 (PLAN17): Added options parameter for batch-awareness
 * @param {Object} updates - Partial state updates
 * @param {string} action - Action name for debugging
 * @param {Object} [options={}] - Additional options passed to subscribers
 * @param {boolean} [options.isBatchUpdate] - If true, this is part of a batch operation
 * @param {boolean} [options.dimensionOnly] - If true, only dimension fields changed
 */
function setState(updates, action = "unknown", options = {}) {
  const prevState = state;
  state = { ...state, ...updates };

  // Notify subscribers with optional options
  subscribers.forEach((callback) => {
    try {
      callback(state, prevState, action, options);
    } catch (err) {
      logError("Subscriber error:", err);
    }
  });

  // Emit generic state change event
  // G3-M1 Fix (PLAN19): Pass through dimensionOnly flag for history filtering
  emit(Events.DATA_CHANGED, { action, changes: Object.keys(updates), ...options });
}

// ============================================
// Data Actions (Tree Operations)
// ============================================

/**
 * Initialize store with new data - FULL RESET.
 *
 * @warning This resets undo history. Only use when:
 * - Loading a new document (doc switch)
 * - Initial app load
 * - Full data replacement scenarios
 *
 * Do NOT use during sync operations - use _setDataFromCRDT() instead.
 *
 * Effects:
 * - Emits DATA_LOADED → resets history stack
 * - Loads UI state from localStorage
 * - Sets all state properties
 *
 * @param {Object} rootNode - Root node of the tree
 * @param {Object} [options] - Additional state to set
 */
export function initialize(rootNode, options = {}) {
  // The map's state is the engine's. Opened there already (engine/open-map.js:
  // its interface sets the screen state in onOpened), only the screen state
  // is set here. Otherwise it is opened there now, followed here in one step
  // with the screen state below (the mirror waits for it).
  if (MapState.get("data") !== rootNode || MapState.get("currentDocId") !== (options.docId || null)) {
    initializing = true;
    try {
      MapState.open(rootNode, { docId: options.docId || null, isShared: options.isShared || false, shareRole: options.shareRole || null });
    } finally {
      initializing = false;
    }
  }

  // Load local view state (transforms, notesPanelOpen, ganttZoom, currentView) - never synced to server
  // Options take priority over localStorage (for preserving state during CRDT updates)
  const docId = options.docId || null;
  const localState = docId ? getDocState(docId) : null;

  // Collect tag IDs actually used in the tree, so we can drop stale persisted
  // hidden/highlighted IDs (e.g. from a pre-migration build). Stale IDs caused
  // the all-dimmed-on-load symptom that motivated commit 75ec262 making
  // highlightedTagIds transient — validating instead lets persistence return.
  const liveTagIds = new Set();
  (function walk(n) {
    if (!n) return;
    if (Array.isArray(n.tags)) {
      for (const t of n.tags) {
        const id = typeof t === "string" ? t : t?.tagId;
        if (id) liveTagIds.add(id);
      }
    }
    if (Array.isArray(n.children)) n.children.forEach(walk);
  })(rootNode);
  const filterValidTagIds = (ids) =>
    Array.isArray(ids)
      ? ids.filter((id) => isNotTaggedId(id) || liveTagIds.has(id))
      : [];

  // Determine focusId with validation - stale local state (e.g., old "root" values) may not exist in tree
  let focusId = options.focusId || localState?.focusId || rootNode.id;
  // Validate focusId exists in tree; fall back to rootNode.id if not found
  if (focusId !== rootNode.id && !dataModel.find(focusId)) {
    logWarn(`focusId "${focusId}" not found in tree, falling back to root`);
    focusId = rootNode.id;
  }

  // Validate selectedId if provided - clear if node doesn't exist.
  // Routine on doc-open with a stale stored selection; debug-level log.
  let selectedId = options.selectedId || null;
  if (selectedId && !dataModel.find(selectedId)) {
    logDebug(`selectedId "${selectedId}" not found in tree, clearing selection`);
    selectedId = null;
  }

  setState(
    {
      data: rootNode,
      focusId,
      selectedId,
      currentView: normalizeViewName(
        options.currentView ||
        localState?.currentView ||
        options.view ||
        "dashboard"
      ),
      hideCompleted: false, // Legacy — replaced by hiddenTagIds (Done tag eye toggle)
      ganttScale: options.ganttScale || localState?.ganttScale || "auto",
      ganttZoom:
        options.ganttZoom !== undefined
          ? options.ganttZoom
          : localState?.ganttZoom || 1,
      ganttRowHeight:
        options.ganttRowHeight !== undefined
          ? options.ganttRowHeight
          : localState?.ganttRowHeight || 100,
      mindmapGap:
        options.mindmapGap !== undefined
          ? options.mindmapGap
          : localState?.mindmapGap || 50,
      mindmapLayoutMode: localState?.mindmapLayoutMode || "balanced",
      userSetProjectDates:
        options.userSetProjectDates !== undefined
          ? options.userSetProjectDates
          : localState?.userSetProjectDates || false,
      notesPanelOpen:
        options.notesPanelOpen !== undefined
          ? options.notesPanelOpen
          : localState?.notesPanelOpen || false,
      currentNoteId:
        options.currentNoteId !== undefined
          ? options.currentNoteId
          : localState?.currentNoteId || null,
      mindmapTransform: options.mindmapTransform ||
        localState?.mindmapTransform || {
          zoom: 1,
          panX: 0,
          panY: 0,
        },
      bubbleTransform: options.bubbleTransform || localState?.bubbleTransform || null,
      currentDocId: docId,
      isShared: options.isShared || false,
      shareRole: options.shareRole || null,
      hiddenTagIds: filterValidTagIds(localState?.hiddenTagIds),
      highlightedTagIds: filterValidTagIds(localState?.highlightedTagIds),
      tagDimLevel: localState?.tagDimLevel ?? 80,
      horizon: normalizeHorizon(localState?.horizon),
    },
    "initialize",
  );

  emit(Events.DATA_LOADED, { data: rootNode });
}

// ============================================
// Navigation Actions
// ============================================

/**
 * Set focus (zoom) to a node
 * @param {string} nodeId - Node ID to focus
 */
export function setFocus(nodeId) {
  const node = dataModel.find(nodeId);
  // rootId is dynamic (not always "root"), e.g., shared docs
  const rootId = state.data?.id;
  if (node || nodeId === rootId) {
    setState({ focusId: nodeId }, "setFocus");
    emit(Events.NODE_FOCUSED, { nodeId });
  }
}

/**
 * Set selected node
 * @param {string|null} nodeId - Node ID or null to clear
 */
export function setSelected(nodeId) {
  setState({ selectedId: nodeId }, "setSelected");
  if (nodeId) {
    emit(Events.NODE_SELECTED, { nodeId });
  }
}

/**
 * Set hovered node
 * @param {string|null} nodeId - Node ID or null to clear
 */
export function setHovered(nodeId) {
  if (state.hoveredId !== nodeId) {
    setState({ hoveredId: nodeId }, "setHovered");
  }
}

/**
 * Navigate up to parent
 * @returns {boolean} True if navigation occurred
 */
export function navigateUp() {
  // rootId is dynamic (not always "root"), e.g., shared docs
  const rootId = state.data?.id;
  if (state.focusId === rootId) {
    return false;
  }
  const parent = dataModel.findParent(state.focusId);
  if (parent) {
    setFocus(parent.id);
    return true;
  }
  return false;
}

/**
 * Navigate into a node (zoom in)
 * @param {string} nodeId - Node to zoom into
 */
export function navigateInto(nodeId) {
  const node = dataModel.find(nodeId);
  if (node && node.children?.length > 0) {
    setFocus(nodeId);
  }
}

// ============================================
// View Actions
// ============================================

/**
 * Switch to a different view
 * @param {string} viewName - View to switch to
 */
export function setView(viewName) {
  viewName = normalizeViewName(viewName);
  if (state.currentView !== viewName) {
    const prevView = state.currentView;
    setState({ currentView: viewName }, "setView");
    emit(Events.VIEW_CHANGED, { view: viewName, previousView: prevView });
  }
}

/**
 * Toggle hide completed tasks
 * @param {boolean} [value] - Explicit value, or toggle if not provided
 */
export function setHideCompleted(value) {
  const newValue = value !== undefined ? value : !state.hideCompleted;
  setState({ hideCompleted: newValue }, "setHideCompleted");
}

/**
 * Set Gantt scale
 * @param {string} scale - 'day', 'week', 'month', or 'auto'
 */
export function setGanttScale(scale) {
  setState({ ganttScale: scale }, "setGanttScale");
}

/**
 * Set Gantt zoom level
 * @param {number} zoom - Zoom level
 */
export function setGanttZoom(zoom) {
  setState({ ganttZoom: zoom }, "setGanttZoom");
}

/**
 * Set Gantt row height
 * @param {number} height - Row height as percentage
 */
export function setGanttRowHeight(height) {
  setState({ ganttRowHeight: height }, "setGanttRowHeight");
}

/**
 * Set mindmap vertical gap
 * @param {number} gap - Gap as percentage of node height
 */
export function setMindmapGap(gap) {
  setState({ mindmapGap: gap }, "setMindmapGap");
}

/**
 * Set the mindmap level-0 layout mode: "balanced" | "right" | "left".
 */
export function setMindmapLayoutMode(mode) {
  setState({ mindmapLayoutMode: mode }, "setMindmapLayoutMode");
}

/**
 * Set user-defined project dates flag
 * @param {boolean} value - Whether user manually set project dates
 */
export function setUserSetProjectDates(value) {
  setState({ userSetProjectDates: value }, "setUserSetProjectDates");
}

/**
 * Set mindmap view transform (zoom/pan)
 * @param {Object} transform - { zoom, panX, panY }
 */
export function setMindmapTransform(transform) {
  setState({ mindmapTransform: { ...transform } }, "setMindmapTransform");
}

/**
 * Set bubble view transform (zoom/pan)
 * @param {Object} transform - { zoom, panX, panY }
 */
export function setBubbleTransform(transform) {
  setState({ bubbleTransform: { ...transform } }, "setBubbleTransform");
}

// ============================================
// UI Actions
// ============================================

/**
 * Toggle sidebar collapsed state
 * @param {boolean} [value] - Explicit value, or toggle if not provided
 */
export function setSidebarCollapsed(value) {
  const newValue = value !== undefined ? value : !state.sidebarCollapsed;
  setState({ sidebarCollapsed: newValue }, "setSidebarCollapsed");
}

/**
 * Set notes panel open state
 * @param {boolean} [value] - Explicit value, or toggle if not provided
 */
export function setNotesPanelOpen(value) {
  const newValue = value !== undefined ? value : !state.notesPanelOpen;
  setState({ notesPanelOpen: newValue }, "setNotesPanelOpen");
}

/**
 * Set current note ID (for notes panel/view)
 * @param {string|null} noteId - Note ID or null to clear
 */
export function setCurrentNoteId(noteId) {
  setState({ currentNoteId: noteId }, "setCurrentNoteId");
}

/**
 * Set hidden tag IDs (nodes with these tags are hidden from view)
 * @param {string[]} ids
 */
export function setHiddenTagIds(ids) {
  setState({ hiddenTagIds: ids }, "setHiddenTagIds");
}

/**
 * Set the horizon cut-off (partial update merged into the current horizon).
 * @param {Partial<{past, ahead, inExecutionOnly}>} partial
 */
export function setHorizon(partial) {
  setState({ horizon: normalizeHorizon({ ...state.horizon, ...(partial || {}) }) }, "setHorizon");
}

/**
 * Set highlighted tag IDs (nodes with these tags get color highlight)
 * @param {string[]} ids
 */
export function setHighlightedTagIds(ids) {
  setState({ highlightedTagIds: ids }, "setHighlightedTagIds");
}

/**
 * Set tag dim level (0-100, how much to dim non-matching nodes)
 * @param {number} level
 */
export function setTagDimLevel(level) {
  setState({ tagDimLevel: level }, "setTagDimLevel");
}

/**
 * Set modal open state
 * @param {boolean} open - Whether modal is open
 * @param {string} [editingNodeId] - Node being edited (null for new)
 */
export function setModalOpen(open, editingNodeId = null) {
  setState(
    {
      modalOpen: open,
      editingNodeId: open ? editingNodeId : null,
    },
    "setModalOpen",
  );

  emit(open ? Events.MODAL_OPEN : Events.MODAL_CLOSE, {
    modalId: "task-modal",
    nodeId: editingNodeId,
  });
}

// ============================================
// User/Document Actions
// ============================================

/**
 * Set current user
 * @param {Object|null} user - User object
 */
export function setCurrentUser(user) {
  setState({ currentUser: user }, "setCurrentUser");
}

/**
 * Set the user's role on the open document (after a live role change).
 * @param {string|null} role - "owner" | "admin" | "editor" | "viewer" | null
 */
export function setShareRole(role) {
  MapState.setShareRole(role);
}

/**
 * Set current document ID
 * @param {string|null} docId - Document ID
 */
export function setCurrentDocId(docId) {
  MapState.setCurrentDocId(docId);
}

// ============================================
/**
 * Update a node directly in Store (bypasses CRDT)
 * Used for local-only data like dimensions in shared docs.
 * Does NOT trigger CRDT writes or history entries, only UI refresh.
 * @param {string} nodeId
 * @param {Object} updates
 * @param {{dimensionOnly?: boolean}} [options] - false when the change shows
 *   (a fold kept on this device): subscribers redraw. Default: sizes only.
 */
export function _updateNodeDirect(nodeId, updates, options) {
  MapState.updateLocalFields(nodeId, updates, options);
}

// Links to other maps: the engine's (engine/map-state.js).
export const isReferenceNode = MapState.isReferenceNode;
export const getLinkedDocId = MapState.getLinkedDocId;
export const isNodeInLinkedDoc = MapState.isNodeInLinkedDoc;
export const getLinkedDocIdForNode = MapState.getLinkedDocIdForNode;
export const initLinkedDocsFromTree = MapState.initLinkedDocsFromTree;
export const clearLinkedDocs = MapState.clearLinkedDocs;

/**
 * Set the tree from the map's CRDT: the engine's (engine/map-state.js
 * setTree), for callers that still come here. Returns false when rejected.
 */
export function _setDataFromCRDT(tree, options = {}) {
  return MapState.setTree(tree, options);
}

// ============================================================
// Following the engine's map state
// ============================================================

let initializing = false;
let resetting = false;

/** The closest item of `previousTree` above `id` that `exists`, or null. */
function closestRemaining(previousTree, id, exists) {
  if (!previousTree) return null;
  const parents = new Map();
  (function walk(node, parent) {
    parents.set(node.id, parent);
    for (const child of node.children || []) walk(child, node);
  })(previousTree, null);
  for (let node = parents.get(id); node; node = parents.get(node.id)) {
    if (exists(node.id)) return node.id;
  }
  return null;
}

on(Events.MAP_STATE_CHANGED, (event) => {
  const map = event.state;
  const mirror = { data: map.data, currentDocId: map.currentDocId, isShared: map.isShared, shareRole: map.shareRole };
  switch (event.reason) {
    case "open":
      // Store.initialize sets this with the screen state, in one step.
      if (!initializing) setState({ ...mirror, focusId: map.data?.id ?? null, selectedId: null }, "mapOpened");
      return;
    case "tree": {
      const tree = map.data;
      const exists = (id) => id === tree.id || !!dataModel.find(id);
      // The focused item gone (removed, here or elsewhere): the closest of
      // its parents still there, else the root.
      let focusId = state.focusId;
      if (!event.preserveFocus || !focusId) focusId = tree.id;
      else if (!exists(focusId)) {
        focusId = closestRemaining(event.previousTree, focusId, exists) || tree.id;
        logDebug(`focus "${state.focusId}" gone, now "${focusId}"`);
      }
      let selectedId = state.selectedId;
      if (selectedId && (!event.preserveSelection || !exists(selectedId))) selectedId = null;
      setState({ ...mirror, focusId, selectedId }, `_setDataFromCRDT:${event.source}`, { isBatchUpdate: !!event.isBatchUpdate });
      return;
    }
    case "local-fields":
      setState({ data: map.data }, "_updateNodeDirect", { dimensionOnly: event.dimensionOnly });
      return;
    case "role":
      setState({ shareRole: map.shareRole }, "setShareRole");
      return;
    case "map-id":
      setState({ currentDocId: map.currentDocId }, "setCurrentDocId");
      return;
    case "reset":
      // The map closed in the engine alone (reset() below sets everything).
      if (!resetting) {
        setState({ data: null, currentDocId: null, isShared: false, shareRole: null, focusId: null, selectedId: null }, "mapClosed");
      }
      return;
    default:
  }
});

// ============================================
// Reset
// ============================================

/**
 * Reset store to initial state
 */
export function reset() {
  resetting = true;
  try {
    MapState.reset();
  } finally {
    resetting = false;
  }
  state = { ...initialState };
  subscribers.forEach((callback) => {
    try {
      callback(state, null, "reset");
    } catch (err) {
      logError("Subscriber error on reset:", err);
    }
  });
}

// ============================================
// Selectors (Computed State)
// ============================================

/**
 * Get the currently focused node
 * @returns {Object|null}
 */
export function getFocusedNode() {
  return dataModel.find(state.focusId);
}

/**
 * Get the currently selected node
 * @returns {Object|null}
 */
export function getSelectedNode() {
  return state.selectedId ? dataModel.find(state.selectedId) : null;
}

/**
 * Get visible nodes based on focus and hideCompleted
 * @returns {Array}
 */
export function getVisibleNodes() {
  const focused = getFocusedNode();
  if (!focused) return [];

  const nodes = [];

  function collect(node) {
    if (state.hideCompleted && _nodeIsDoneFn(node)) {
      return;
    }
    nodes.push(node);
    if (node.children && !node.collapsed) {
      node.children.forEach(collect);
    }
  }

  // rootId is dynamic (not always "root"), e.g., shared docs
  const rootId = state.data?.id;
  if (state.focusId === rootId && state.data) {
    state.data.children?.forEach(collect);
  } else if (focused) {
    focused.children?.forEach(collect);
  }

  return nodes;
}

/**
 * Get breadcrumb path to current focus
 * @returns {Array} Array of nodes from root to focus
 */
export function getBreadcrumbs() {
  return dataModel.getPath(state.focusId);
}

/**
 * Check if the current view is read-only (viewer role on a shared doc, the
 * storage quota, or a full map). UI components can use this to disable edit
 * buttons/actions
 * @returns {boolean}
 */
export function isReadOnly() {
  return MapState.isReadOnly();
}

/**
 * Read-only in a way that also stops deleting and cutting: viewer role or
 * storage quota. A full map is read-only but allows them, to make room.
 * @returns {boolean}
 */
export function isDeleteBlocked() {
  return MapState.isDeleteBlocked();
}

/** The open map has reached the size limit (engine/services/map-size.js). */
export function isMapFull() {
  return MapState.isMapFull();
}

// ============================================
// Export data-model functions for convenience
// ============================================

export const find = dataModel.find;
export const findParent = dataModel.findParent;
export const getAll = dataModel.getAll;
export const getPath = dataModel.getPath;
export const getLeaves = dataModel.getLeaves;

// Refresh edit affordances only when their quota state changes.
on(Events.QUOTA_CHANGED, () => {
  const quotaReadOnly = isDocQuotaBlocked(state.currentDocId);
  if (state.quotaReadOnly !== quotaReadOnly) setState({ quotaReadOnly }, 'quotaChanged');
});

// Same when the open map becomes full, or editable again.
on(Events.MAP_SIZE_CHANGED, () => {
  const mapFull = mapIsFull(state.currentDocId);
  if (state.mapFull !== mapFull) setState({ mapFull }, 'mapSizeChanged');
});
